import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon,
  EyeIcon,
  DocumentIcon,
  UserIcon,
  EnvelopeIcon,
  TrophyIcon,
  PencilIcon,
  XMarkIcon,
  ChevronDownIcon,
  DocumentDuplicateIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { getApplication, updateApplicationStatus, deleteApplication, onAuthStateChange, reassignApplicationId, generateNextApplicationId, updateApplicationData } from '../services/firebaseService';
import { Application } from '../types';
import { formatDateTime } from '../utils/helpers';
import {
  sendStatusUpdateEmail,
  sendDisqualificationEmail
} from '../services/emailService';
import { getGlobalSettings } from '../services/settingsService';
import { getCertificateTemplate } from '../services/certificateService';
import { generateCertificate } from '../utils/certificateGenerator';
import { generateConfirmationLetter, generateScheduleLetter } from '../utils/letterGenerator';

// Modular Components
import PersonalInfoSection from '../components/admin/ApplicationDetails/PersonalInfoSection';
import ContactInfoSection from '../components/admin/ApplicationDetails/ContactInfoSection';
import EducationSection from '../components/admin/ApplicationDetails/EducationSection';
import DocumentSection from '../components/admin/ApplicationDetails/DocumentSection';
import AdminHeader from '../components/admin/AdminHeader';

const ApplicationDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const [application, setApplication] = useState<Application | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject'>('approve');
  const [adminUser, setAdminUser] = useState<any>(null);
  const [settings, setSettings] = useState<any>(null);

  // Edit states
  const [isEditing, setIsEditing] = useState(false);
  const [editedApp, setEditedApp] = useState<Application | null>(null);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [copiedId, setCopiedId] = useState(false);

  const navigate = useNavigate();
  const role = adminUser?.role || 'admin';

  useEffect(() => {
    const stored = localStorage.getItem('adminUser');
    if (!stored) {
      navigate('/admin/login');
      return;
    }
    if (stored) {
      setAdminUser(JSON.parse(stored));
    }

    const fetchApplication = async () => {
      if (!id) return;

      try {
        const [app, settingsData] = await Promise.all([
          getApplication(id),
          getGlobalSettings()
        ]);

        if (app) {
          setApplication(app);
          setReviewNotes(app.reviewNotes || '');
        }
        setSettings(settingsData);
      } catch (error) {
        console.error('Error fetching application:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchApplication();

    const unsubscribe = onAuthStateChange((user) => {
      if (!user) {
        if (!localStorage.getItem('adminUser')) {
          navigate('/admin/login');
        }
      }
    });

    return () => unsubscribe();
  }, [id, navigate]);

  const handleStatusUpdate = async (action: 'verify' | 'reject' | 'hold' | 'clarify' | 'review') => {
    if (!application || !id) return;

    setUpdating(true);
    try {
      const actor = adminUser || JSON.parse(localStorage.getItem('adminUser') || '{}');

      let newStatus: Application['status'] = application.status;
      switch (action) {
        case 'verify': newStatus = 'verified'; break;
        case 'reject': newStatus = 'rejected'; break;
        case 'hold': newStatus = 'on_hold'; break;
        case 'clarify': newStatus = 'clarification_requested'; break;
        case 'review': newStatus = 'under_review'; break;
      }

      // Update application status in database
      await updateApplicationStatus(
        id,
        newStatus,
        reviewNotes,
        actor.email || 'admin',
        actor.role || 'admin'
      );

      // Update local state
      const updatedApplication: Application = {
        ...application,
        status: newStatus,
        reviewNotes,
        updatedAt: new Date()
      };
      setApplication(updatedApplication);

      // Send email notification if enabled in settings
      if (settings?.emailNotificationsEnabled) {
        try {
          console.log('Sending status update email notification...');
          let pdfBlob: Blob | undefined;
          let pdfName: string | undefined;

          // If verified, attach the 3-page Training Confirmation Letter
          if (newStatus === 'verified') {
            try {
              console.log('Generating 3-page Training Confirmation Letter for attachment...');
              const doc = await generateConfirmationLetter(updatedApplication);
              pdfBlob = doc.output('blob');
              pdfName = `Training_Confirmation_${application.firstName}_${application.id}.pdf`;
            } catch (genErr) {
              console.error('Error generating PDF for email:', genErr);
            }
          }

          await sendStatusUpdateEmail(updatedApplication, newStatus, reviewNotes, pdfBlob, pdfName);
        } catch (emailError) {
          console.error('Error sending status update email:', emailError);
        }
      }

      setShowReviewModal(false);
      alert(`Registration status updated to ${newStatus.replace('_', ' ')} successfully!`);
    } catch (error) {
      console.error('Error updating application status:', error);
      alert('Error updating application status. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleReassignId = async () => {
    if (!application || !id) return;

    const confirm = window.confirm(
      `Special Registration Number Allotment\n\n` +
      `This will change the registration number from ${id} to the next available reserved/special number.\n\n` +
      `Proceed?`
    );

    if (!confirm) return;

    setUpdating(true);
    try {
      // Get the next special ID (isAdmin = true)
      const nextSpecialId = await generateNextApplicationId(true);

      const proceed = window.confirm(`Found available special ID: ${nextSpecialId}\n\nDo you want to allot this number to ${application.firstName}?`);

      if (proceed) {
        await reassignApplicationId(id, nextSpecialId);
        alert(`Successfully reassigned ID to ${nextSpecialId}`);
        navigate(`/admin/applications/${nextSpecialId}`);
      }
    } catch (error: any) {
      console.error('Error reassigning ID:', error);
      alert(error.message || 'Failed to reassign ID.');
    } finally {
      setUpdating(false);
    }
  };

  const handleGenerateCertificate = async () => {
    if (!application) return;
    setUpdating(true);
    try {
      const template = await getCertificateTemplate();
      const doc = await generateCertificate(application, template);
      doc.save(`Certificate_${application.firstName}_${application.id}.pdf`);
    } catch (error) {
      console.error('Error generating certificate:', error);
      alert('Failed to generate certificate.');
    } finally {
      setUpdating(false);
    }
  };

  const handleGenerateConfirmationLetter = async () => {
    if (!application) return;
    setUpdating(true);
    try {
      const doc = await generateConfirmationLetter(application);
      doc.save(`Confirmation_Letter_${application.firstName}_${application.id}.pdf`);
    } catch (error) {
      console.error('Error generating confirmation letter:', error);
      alert('Failed to generate confirmation letter.');
    } finally {
      setUpdating(false);
    }
  };

  const handleGenerateScheduleLetter = async () => {
    if (!application) return;
    setUpdating(true);
    try {
      const doc = await generateScheduleLetter(application);
      doc.save(`Training_Schedule_${application.firstName}_${application.id}.pdf`);
    } catch (error) {
      console.error('Error generating schedule letter:', error);
      alert('Failed to generate schedule letter.');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteApplication = async () => {
    if (!application || !id) return;

    const secretCode = window.prompt(`To delete this registration, please enter the secret code:`);
    if (secretCode === null) return; // Prompt cancelled
    if (secretCode !== 'KSAI2026') {
      alert('Incorrect secret code. Deletion cancelled.');
      return;
    }

    const reason = window.prompt(`Enter the reason for disqualification/deletion of this registration:`);
    if (reason === null) return; // Cancelled
    if (!reason.trim()) {
      alert('A reason is required to notify the applicant. Deletion cancelled.');
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete this registration?\n\nApplicant: ${application.firstName} ${application.lastName}\nID: ${application.id}\n\nThis action cannot be undone and will send a disqualification email.`
    );

    if (!confirmDelete) return;

    setUpdating(true);
    try {
      // Send the email first before the record is deleted
      await sendDisqualificationEmail(application, reason);

      await deleteApplication(id);
      alert('Registration disqualified and deleted successfully! Notification email sent.');
      navigate('/admin/applications');
    } catch (error) {
      console.error('Error deleting application:', error);
      alert('Error deleting application. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleSendSelectionEmail = async () => {
    if (!application) return;
    const trainingDate = window.prompt('Enter training date (e.g., 20th June 2024):', '');
    const venue = window.prompt('Enter training venue:', 'Kalinga Stadium, Bhubaneswar');

    if (!trainingDate || !venue) return;

    setUpdating(true);
    try {
      const { sendSelectionConfirmationEmail } = await import('../services/emailService');
      const success = await sendSelectionConfirmationEmail(application, trainingDate, venue);
      if (success) {
        alert('Selection confirmation email sent successfully!');
      } else {
        alert('Failed to send email. Check SMTP settings.');
      }
    } catch (error) {
      console.error('Error sending selection email:', error);
      alert('Error sending email.');
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editedApp || !id) return;
    setUpdating(true);
    try {
      await updateApplicationData(id, editedApp);
      setApplication(editedApp);
      setIsEditing(false);
      alert('Registration updated successfully!');
    } catch (error) {
      console.error('Error saving edits:', error);
      alert('Failed to save changes.');
    } finally {
      setUpdating(false);
    }
  };

  const handleManualEmailTrigger = async () => {
    if (!application || !id) return;

    setUpdating(true);
    try {
      let success = false;

      // Determine what email to send based on current status
      if (application.status === 'submitted') {
        const confirm = window.confirm(`Send "Registration Confirmed" email to ${application.firstName} (${application.emailId})?`);
        if (!confirm) {
          setUpdating(false);
          return;
        }

        console.log('Manually sending registration confirmation email...');
        const { sendApplicationReceivedEmail } = await import('../services/emailService');
        success = await sendApplicationReceivedEmail(application);
      } else {
        // Status updates: verified, rejected, on_hold, clarification_requested, under_review
        const confirm = window.confirm(`Send "${application.status.replace(/_/g, ' ')}" status update email to ${application.firstName} (${application.emailId})?`);
        if (!confirm) {
          setUpdating(false);
          return;
        }

        console.log('Manually sending status update email...');
        let pdfBlob: Blob | undefined;
        let pdfName: string | undefined;

        // If verified, generate the Training Confirmation Letter PDF
        if (application.status === 'verified') {
          try {
            console.log('Generating Training Confirmation Letter PDF for manual email attachment...');
            const doc = await generateConfirmationLetter(application);
            pdfBlob = doc.output('blob');
            pdfName = `Training_Confirmation_${application.firstName}_${application.id}.pdf`;
          } catch (genErr) {
            console.error('Error generating PDF for email:', genErr);
          }
        }

        const { sendStatusUpdateEmail } = await import('../services/emailService');
        success = await sendStatusUpdateEmail(
          application,
          application.status,
          application.reviewNotes || reviewNotes,
          pdfBlob,
          pdfName
        );
      }

      if (success) {
        alert('Email sent successfully!');
      } else {
        alert('Failed to send email. Please check your SMTP settings in Admin Settings.');
      }

      // Refresh application state to show the updated email log
      const app = await getApplication(id);
      if (app) setApplication(app);
    } catch (error) {
      console.error('Error in manual email trigger:', error);
      alert('An error occurred while sending the email.');
    } finally {
      setUpdating(false);
    }
  };

  const updateEditedField = (field: string, value: any) => {
    if (!editedApp) return;
    setEditedApp({ ...editedApp, [field]: value });
  };

  const handleDocumentOpen = (url: string) => {
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Application not found</p>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="mt-4 text-primary-600 hover:text-primary-800"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Unified Admin Navigation Header */}
      <AdminHeader
        activeNav="applications"
        title="Candidate Details"
        subtitle={`Reg: ${application.registrationNumber || application.id}`}
      />

      {/* Candidate Action Header Banner */}
      <div className="bg-white border-b border-slate-200/80 shadow-sm sticky top-16 sm:top-20 z-30 transition-all">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            {/* Left: Back & Candidate Brief */}
            <div className="flex items-center space-x-3 min-w-0">
              <button
                type="button"
                onClick={() => navigate('/admin/applications')}
                className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition flex-shrink-0"
                title="Back to Registrations"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>

              <div className="min-w-0">
                <div className="flex items-center space-x-2">
                  <h1 className="text-base sm:text-lg font-black text-slate-900 truncate">
                    {application.firstName} {application.lastName}
                  </h1>
                  <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded-full uppercase ${application.status === 'verified' ? 'bg-emerald-100 text-emerald-800' :
                      application.status === 'rejected' ? 'bg-rose-100 text-rose-800' :
                        application.status === 'under_review' ? 'bg-amber-100 text-amber-800' :
                          'bg-blue-100 text-blue-800'
                    }`}>
                    {application.status.replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="flex items-center space-x-2 text-xs text-slate-500 font-mono mt-0.5">
                  <span className="font-bold text-slate-700">{application.registrationNumber || application.id}</span>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(application.registrationNumber || application.id);
                      setCopiedId(true);
                      setTimeout(() => setCopiedId(false), 2000);
                    }}
                    className="text-slate-400 hover:text-slate-600 transition"
                    title="Copy Reg ID"
                  >
                    {copiedId ? (
                      <CheckIcon className="w-3.5 h-3.5 text-emerald-600" />
                    ) : (
                      <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                    )}
                  </button>
                  <span>&bull;</span>
                  <span className="text-slate-400 font-sans">{application.address?.district || 'Odisha'}</span>
                </div>
              </div>
            </div>

            {/* Right: Actions Buttons (Desktop & Mobile) */}
            <div className="flex items-center space-x-2 flex-wrap">
              {/* If currently editing form */}
              {isEditing ? (
                <>
                  <button
                    onClick={handleSaveEdit}
                    disabled={updating}
                    className="flex-1 sm:flex-none bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center justify-center space-x-1.5 shadow-sm disabled:opacity-50"
                  >
                    <CheckCircleIcon className="w-4 h-4" />
                    <span>Save Changes</span>
                  </button>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setEditedApp(application);
                    }}
                    disabled={updating}
                    className="flex-1 sm:flex-none bg-slate-200 hover:bg-slate-300 text-slate-700 px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center justify-center space-x-1"
                  >
                    <XMarkIcon className="w-4 h-4" />
                    <span>Cancel</span>
                  </button>
                </>
              ) : (
                <>
                  {/* Accounts forwarding */}
                  {application.status === 'submitted' && ['accounts', 'admin', 'super_admin'].includes(role) && (
                    <button
                      onClick={() => {
                        const confirm = window.confirm("Forward to Admin?\n\nThis will verify the candidate's correctness and manual payment status, forwarding to Admin queue for final approval.");
                        if (confirm) {
                          setReviewNotes("Form correctness and manual payment verified.");
                          handleStatusUpdate('review');
                        }
                      }}
                      disabled={updating}
                      className="bg-amber-600 hover:bg-amber-700 text-white px-3.5 py-2 rounded-xl text-xs font-bold flex items-center space-x-1.5 transition shadow-sm disabled:opacity-50"
                    >
                      <span>Verify &amp; Forward</span>
                    </button>
                  )}

                  {/* Admin decision buttons */}
                  {application.status === 'under_review' && ['admin', 'super_admin'].includes(role) && (
                    <div className="flex items-center space-x-1.5">
                      <button
                        onClick={() => {
                          setReviewAction('approve');
                          setShowReviewModal(true);
                        }}
                        disabled={updating}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-2 rounded-xl text-xs font-bold flex items-center space-x-1.5 transition shadow-sm disabled:opacity-50"
                      >
                        <CheckCircleIcon className="w-4 h-4" />
                        <span>Verify &amp; Approve</span>
                      </button>
                      <button
                        onClick={() => {
                          setReviewAction('reject');
                          setShowReviewModal(true);
                        }}
                        disabled={updating}
                        className="bg-rose-600 hover:bg-rose-700 text-white px-3.5 py-2 rounded-xl text-xs font-bold flex items-center space-x-1.5 transition shadow-sm disabled:opacity-50"
                      >
                        <XCircleIcon className="w-4 h-4" />
                        <span>Reject</span>
                      </button>
                    </div>
                  )}

                  {/* Print View button */}
                  <button
                    onClick={() => navigate(`/admin/applications/${application.id}/print`)}
                    className="bg-slate-100 hover:bg-slate-200 text-slate-800 px-3 py-2 rounded-xl text-xs font-bold transition flex items-center space-x-1.5 border border-slate-200"
                  >
                    <EyeIcon className="w-4 h-4 text-slate-600" />
                    <span className="hidden sm:inline">Print View</span>
                  </button>

                  {/* Actions Dropdown Button */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowActionMenu(!showActionMenu)}
                      className="bg-primary-50 hover:bg-primary-100 text-primary-700 border border-primary-200 px-3 py-2 rounded-xl text-xs font-bold transition flex items-center space-x-1.5"
                    >
                      <span>More Actions</span>
                      <ChevronDownIcon className="w-3.5 h-3.5 text-primary-500" />
                    </button>

                    {showActionMenu && (
                      <div className="absolute right-0 mt-2 w-64 rounded-2xl bg-white shadow-xl border border-slate-100 py-2 z-50 animate-in fade-in zoom-in-95 duration-150">
                        {['deo', 'admin', 'super_admin'].includes(role) && (
                          <button
                            onClick={() => {
                              setShowActionMenu(false);
                              setIsEditing(true);
                              setEditedApp(application);
                            }}
                            className="w-full text-left px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center space-x-2"
                          >
                            <PencilIcon className="w-4 h-4 text-slate-400" />
                            <span>Edit Application Form</span>
                          </button>
                        )}

                        <button
                          onClick={() => {
                            setShowActionMenu(false);
                            handleManualEmailTrigger();
                          }}
                          className="w-full text-left px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center space-x-2"
                        >
                          <EnvelopeIcon className="w-4 h-4 text-slate-400" />
                          <span>Resend Status Email</span>
                        </button>

                        {application.status === 'verified' && (
                          <>
                            <div className="border-t border-slate-100 my-1 pt-1">
                              <p className="px-4 py-1 text-[10px] font-extrabold uppercase text-slate-400">Letters &amp; Documents</p>
                              <button
                                onClick={() => {
                                  setShowActionMenu(false);
                                  handleGenerateConfirmationLetter();
                                }}
                                className="w-full text-left px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center space-x-2"
                              >
                                <DocumentIcon className="w-4 h-4 text-indigo-500" />
                                <span>Confirmation Letter PDF</span>
                              </button>
                              <button
                                onClick={() => {
                                  setShowActionMenu(false);
                                  handleGenerateScheduleLetter();
                                }}
                                className="w-full text-left px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center space-x-2"
                              >
                                <DocumentIcon className="w-4 h-4 text-teal-500" />
                                <span>Training Schedule Letter</span>
                              </button>
                              <button
                                onClick={() => {
                                  setShowActionMenu(false);
                                  handleGenerateCertificate();
                                }}
                                className="w-full text-left px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center space-x-2"
                              >
                                <TrophyIcon className="w-4 h-4 text-amber-500" />
                                <span>Generate Certificate PDF</span>
                              </button>
                              <button
                                onClick={() => {
                                  setShowActionMenu(false);
                                  handleSendSelectionEmail();
                                }}
                                className="w-full text-left px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center space-x-2"
                              >
                                <EnvelopeIcon className="w-4 h-4 text-emerald-500" />
                                <span>Send Selection Email</span>
                              </button>
                            </div>
                          </>
                        )}

                        {role === 'super_admin' && (
                          <div className="border-t border-slate-100 my-1 pt-1">
                            <button
                              onClick={() => {
                                setShowActionMenu(false);
                                handleReassignId();
                              }}
                              className="w-full text-left px-4 py-2 text-xs font-bold text-purple-700 hover:bg-purple-50 flex items-center space-x-2"
                            >
                              <CheckCircleIcon className="w-4 h-4 text-purple-500" />
                              <span>Allot Special Reg ID</span>
                            </button>
                          </div>
                        )}

                        {['admin', 'super_admin'].includes(role) && (
                          <div className="border-t border-slate-100 my-1 pt-1">
                            <button
                              onClick={() => {
                                setShowActionMenu(false);
                                handleDeleteApplication();
                              }}
                              className="w-full text-left px-4 py-2 text-xs font-bold text-rose-600 hover:bg-rose-50 flex items-center space-x-2"
                            >
                              <TrashIcon className="w-4 h-4 text-rose-500" />
                              <span>Disqualify &amp; Delete</span>
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <main className="flex-1 max-w-7xl w-full mx-auto py-5 sm:py-8 px-3 sm:px-6 lg:px-8 pb-24 lg:pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Application Status */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${application.status === 'verified'
                      ? 'bg-green-100'
                      : application.status === 'rejected'
                        ? 'bg-red-100'
                        : application.status === 'under_review'
                          ? 'bg-yellow-100'
                          : 'bg-blue-100'
                    }`}>
                    {application.status === 'verified' ? (
                      <CheckCircleIcon className="w-6 h-6 text-green-600" />
                    ) : application.status === 'rejected' ? (
                      <XCircleIcon className="w-6 h-6 text-red-600" />
                    ) : (
                      <UserIcon className="w-6 h-6 text-blue-600" />
                    )}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {application.firstName} {application.lastName}
                    </h2>
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${application.status === 'verified'
                        ? 'bg-green-100 text-green-800'
                        : application.status === 'rejected'
                          ? 'bg-red-100 text-red-800'
                          : application.status === 'under_review'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-blue-100 text-blue-800'
                      }`}>
                      {application.status.replace(/_/g, ' ').charAt(0).toUpperCase() + application.status.replace(/_/g, ' ').slice(1)}
                    </span>
                  </div>
                </div>

                <div className="text-right">
                  <p className="text-sm text-gray-600">Submitted on</p>
                  <p className="font-medium">{formatDateTime(application.submittedAt)}</p>
                  {application.reviewedAt && (
                    <>
                      <p className="text-sm text-gray-600 mt-2">Reviewed on</p>
                      <p className="font-medium">{formatDateTime(application.reviewedAt)}</p>
                      {application.reviewedBy && (
                        <p className="text-xs text-gray-500">by {application.reviewedBy}</p>
                      )}
                    </>
                  )}
                </div>
              </div>

              {application.reviewNotes && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Review Notes</h4>
                  <p className="text-gray-700">{application.reviewNotes}</p>
                </div>
              )}

              {/* Email Notification Log Status Banner */}
              <div className="mt-6 border-t border-gray-100 pt-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-start space-x-3">
                    <EnvelopeIcon className={`w-5 h-5 mt-0.5 ${application.emailLog?.status === 'sent'
                        ? 'text-green-600'
                        : application.emailLog?.status === 'failed'
                          ? 'text-red-600'
                          : 'text-gray-500'
                      }`} />
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">Email Notification Status</h4>
                      {application.emailLog ? (
                        <div className="mt-1 text-sm text-gray-600">
                          {application.emailLog.status === 'sent' && (
                            <span className="inline-flex items-center text-green-700 bg-green-50 px-2 py-0.5 rounded text-xs font-medium border border-green-200">
                              Sent Successfully
                            </span>
                          )}
                          {application.emailLog.status === 'failed' && (
                            <span className="inline-flex items-center text-red-700 bg-red-50 px-2 py-0.5 rounded text-xs font-medium border border-red-200">
                              Sending Failed
                            </span>
                          )}
                          {application.emailLog.status === 'pending' && (
                            <span className="inline-flex items-center text-yellow-700 bg-yellow-50 px-2 py-0.5 rounded text-xs font-medium border border-yellow-200">
                              Pending
                            </span>
                          )}
                          <span className="text-xs text-gray-500 ml-2">
                            Last attempt: {formatDateTime(application.emailLog.lastAttemptAt)}
                          </span>
                          {application.emailLog.error && (
                            <p className="text-xs text-red-600 mt-1.5 bg-red-50 p-2 rounded border border-red-100 font-mono break-all">
                              Error: {application.emailLog.error}
                            </p>
                          )}
                          {application.emailLog.messageId && (
                            <p className="text-xs text-gray-500 mt-1 font-mono">
                              Nodemailer ID: {application.emailLog.messageId}
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500 mt-0.5">
                          No email has been dispatched or logged for this registration.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex-shrink-0">
                    <button
                      onClick={handleManualEmailTrigger}
                      disabled={updating}
                      className={`inline-flex items-center space-x-2 px-3.5 py-2 border text-xs font-semibold rounded-lg shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 ${application.emailLog?.status === 'sent'
                          ? 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-gray-500'
                          : application.emailLog?.status === 'failed'
                            ? 'border-red-300 text-red-700 bg-red-50 hover:bg-red-100 focus:ring-red-500'
                            : 'border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:ring-indigo-500'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      title="Manually send or resend email notification to candidate"
                    >
                      <EnvelopeIcon className="w-4 h-4" />
                      <span>
                        {application.emailLog?.status === 'sent'
                          ? 'Resend Email'
                          : application.emailLog?.status === 'failed'
                            ? 'Retry Dispatch'
                            : 'Send Email Manually'}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Debug: show detected role and status */}
              <div className="mt-4 text-xs text-gray-500 border-t border-gray-100 pt-3">
                <span>Detected role: {(adminUser?.role || 'admin')}</span>
                <span className="ml-3">Status: {application.status}</span>
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <PersonalInfoSection
            application={isEditing ? editedApp! : application}
            isEditing={isEditing}
            onUpdate={updateEditedField}
          />

          {/* Contact Information */}
          <ContactInfoSection
            application={isEditing ? editedApp! : application}
            isEditing={isEditing}
            onUpdate={updateEditedField}
          />

          {/* Education Details */}
          <EducationSection
            application={isEditing ? editedApp! : application}
            isEditing={isEditing}
            onUpdate={updateEditedField}
          />

          {/* Documents */}
          <DocumentSection
            application={isEditing ? editedApp! : application}
            isEditing={isEditing}
            onUpdateDocument={(key, url) => {
              if (editedApp) {
                const updatedDocs = {
                  ...editedApp.documents,
                  [key]: url
                };
                setEditedApp({
                  ...editedApp,
                  documents: updatedDocs
                });
              }
            }}
            onUpdateSportsCertificates={(urls) => {
              if (editedApp) {
                const updatedDocs = {
                  ...editedApp.documents,
                  sportsCertificates: urls
                };
                setEditedApp({
                  ...editedApp,
                  documents: updatedDocs
                });
              }
            }}
            onOpenDocument={handleDocumentOpen}
          />
        </div>
      </main>

      {/* Review Modal */}
      {showReviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {reviewAction === 'approve' ? 'Verify' : 'Reject'} Registration
            </h3>

            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Action Type
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setReviewAction('approve')}
                  className={`px-3 py-2 text-sm rounded-lg border ${reviewAction === 'approve' ? 'bg-green-50 border-green-500 text-green-700' : 'border-gray-200'}`}
                >
                  Verify / Approve
                </button>
                <button
                  onClick={() => setReviewAction('reject')}
                  className={`px-3 py-2 text-sm rounded-lg border ${reviewAction === 'reject' ? 'bg-red-50 border-red-500 text-red-700' : 'border-gray-200'}`}
                >
                  Reject
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Review Notes {reviewAction === 'reject' && <span className="text-red-500">*</span>}
              </label>
              <textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder={`Add notes for ${reviewAction}...`}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowReviewModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={updating}
              >
                Cancel
              </button>

              {reviewAction === 'approve' ? (
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleStatusUpdate('hold')}
                    disabled={updating}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  >
                    On Hold
                  </button>
                  <button
                    onClick={() => handleStatusUpdate('verify')}
                    disabled={updating}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Verify
                  </button>
                </div>
              ) : (
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleStatusUpdate('clarify')}
                    disabled={updating}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                  >
                    Clarify
                  </button>
                  <button
                    onClick={() => handleStatusUpdate('reject')}
                    disabled={updating || !reviewNotes.trim()}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Reject
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationDetailsPage;